"use client";

import {JSX, ReactNode, useEffect} from "react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {DestinationCard, DestinationCardProps} from "@/entities/destination/ui/card/destination-card";

import {useModel, useModelScroll} from "@/shared/hooks";
import {MOBILE_MAX, TABLET_LANDSCAPE_MAX, useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import {DestinationListInstance, DestinationList as DestinationListModel} from "./destination.model";
import {ShowMoreButton, ShuffleButton, ViewAllButton} from "./parts";

import "./styles/destination.scss";

// Export button components
export {ViewAllButton, ShowMoreButton, ShuffleButton} from "./parts";

export interface DestinationListActions {
    shuffle: () => void;
    showLess: () => void;
    loadMore: () => void;
    handleShowMore: () => void;
    showAll: () => void;
}

export interface DestinationListRenderProps<T = DestinationCardProps> {
    actions: DestinationListActions;
    model: DestinationListInstance<T>;
    visibleDestinations: Array<T>;
    hasMore: boolean;
    showButton: boolean;
    totalItems: number;
}
type RenderFunction<P> = (props: P) => ReactNode;

export interface DestinationProps {
    title: string;
    description: string;
    destinations: Array<DestinationCardProps>;
    space?: SpaceProps;
    // Options instead of render functions (for Server Components compatibility)
    showShuffle?: boolean;
    showViewAll?: boolean;
    viewAllLink?: string;
    showShowMore?: boolean;
    // CTA link (external navigation)
    ctaLink?: {
        url: string;
        text?: string;
        target?: "_self" | "_blank";
    };
    // Legacy render functions (for Client Components)
    renderHeader?: RenderFunction<DestinationListRenderProps>;
    renderActions?: RenderFunction<DestinationListRenderProps>;
    renderButton?: RenderFunction<DestinationListRenderProps>;
    anchorId?: string;
    // Display settings
    responsive?: {
        desktop?: number;
        tablet?: number;
        mobile?: number;
    };
}

export function DestinationList({
    title,
    description,
    destinations,
    space,
    showShuffle = false,
    showViewAll = false,
    viewAllLink,
    showShowMore = false,
    ctaLink,
    renderHeader,
    renderActions,
    renderButton,
    anchorId,
    responsive,
}: DestinationProps): JSX.Element {
    const desktopCount = responsive?.desktop ?? 4;
    const tabletCount = responsive?.tablet ?? 3;
    const mobileCount = responsive?.mobile ?? 4;

    const sectionVars = useSectionSpace(space);
    const isMobile = useMatchMedia({maxWidth: MOBILE_MAX});
    const isTablet = useMatchMedia({maxWidth: TABLET_LANDSCAPE_MAX});

    const initialCount = isMobile ? mobileCount : isTablet ? tabletCount : desktopCount;

    const [model, snapshot] = useModel(DestinationListModel, {
        destinations,
        itemsPerPage: initialCount,
    });

    useEffect(() => {
        if (isMobile) {
            model.setItemsPerPage(mobileCount);
        } else if (isTablet) {
            model.setItemsPerPage(tabletCount);
        } else {
            model.setItemsPerPage(desktopCount);
        }
    }, [isMobile, isTablet]);

    const sectionRef = useModelScroll<HTMLDivElement>(model, {
        eventName: "scrollToTop",
        offset: -100,
    });

    const buttonRef = useModelScroll<HTMLDivElement>(model, {
        eventName: "scrollToButton",
        offset: -500,
    });

    const actions: DestinationListActions = {
        showAll: () => model.showAll(),
        shuffle: () => model.shuffle(),
        showLess: () => model.showLess(),
        loadMore: () => model.loadMore(),
        handleShowMore: () => model.handleShowMore(),
    };

    const renderProps: DestinationListRenderProps = {
        actions,
        model,
        visibleDestinations: snapshot.visibleDestinations,
        hasMore: snapshot.hasMore,
        showButton: snapshot.showButton,
        totalItems: snapshot.totalItems,
    };

    // Build actions based on options (if renderActions not provided)
    // These buttons appear in header on desktop, in bottom section on mobile
    const defaultActions = [];
    if (showShuffle) {
        defaultActions.push(<ShuffleButton key="shuffle" {...renderProps} />);
    }
    if (showViewAll) {
        defaultActions.push(<ViewAllButton key="viewAll" {...renderProps} viewAllLink={viewAllLink} />);
    }

    // Build button based on options (if renderButton not provided)
    // All buttons are independent - show both CTA and ShowMore if both are enabled
    let defaultButton: ReactNode = null;
    const buttons: Array<ReactNode> = [];

    // Add shuffle and viewAll buttons to bottom section (for mobile)
    if (showShuffle) {
        buttons.push(<ShuffleButton key="shuffle-mobile" {...renderProps} />);
    }
    if (showViewAll) {
        buttons.push(<ViewAllButton key="viewAll-mobile" {...renderProps} viewAllLink={viewAllLink} />);
    }

    // CTA button (always shown if enabled, regardless of item count)
    if (ctaLink?.url) {
        buttons.push(
            <ButtonBase
                key="cta"
                as="link"
                href={ctaLink.url}
                target={ctaLink.target || "_self"}
                variant="secondary-outline"
                size="sm"
                width="auto"
                className="destination__button-cta"
            >
                {ctaLink.text || "View All"}
            </ButtonBase>,
        );
    }

    // ShowMore button (only shown if enabled and there are more than 4 items)
    if (showShowMore && snapshot.showButton) {
        buttons.push(<ShowMoreButton key="showMore" {...renderProps} />);
    }

    // Wrap buttons if there are multiple, otherwise return single button
    if (buttons.length > 0) {
        defaultButton =
            buttons.length > 1 ? <div style={{display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap"}}>{buttons}</div> : buttons[0];
    }

    return (
        <section className="destination" style={sectionVars} ref={sectionRef} id={anchorId}>
            <ContainerBase>
                <div className="destination__container">
                    {renderHeader ? (
                        renderHeader(renderProps)
                    ) : (
                        <div className="destination__header destination__header_notbreak">
                            <div className="destination__header-main">
                                <Typography as="h2" size={{desktop: 24, mobile: 18}} className="destination__title destination__description_left">
                                    {title}
                                </Typography>
                                <Typography
                                    as="p"
                                    color="paragraph"
                                    weight={400}
                                    size={{desktop: 14, mobile: 12}}
                                    className="destination__description destination__description_left"
                                >
                                    {description}
                                </Typography>
                            </div>
                            {renderActions ? renderActions(renderProps) : defaultActions.length > 0 ? <>{defaultActions}</> : null}
                        </div>
                    )}
                    <div className="destination__cards">
                        {snapshot.visibleDestinations.map((destination) => (
                            <DestinationCard key={destination.id} {...destination} />
                        ))}
                    </div>
                </div>

                {(renderButton || defaultButton) && (
                    <div className="destination__button-wrap" ref={buttonRef}>
                        {renderButton ? renderButton(renderProps) : defaultButton}
                    </div>
                )}
            </ContainerBase>
        </section>
    );
}
